
import { useState } from 'react'
import { Button } from '../ui'
import { askGemini } from '../../lib/gemini'
import { Sparkles, Trophy, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const WinnerPredictor = ({ pouleStandings }) => {
    const { t, i18n } = useTranslation()
    const [prediction, setPrediction] = useState(null)
    const [loading, setLoading] = useState(false)

    const predictWinner = async () => {
        setLoading(true)
        setPrediction(null)

        try {
            // Convert stats to simplified string
            let statsText = t('presentation_page.predictor_stats_header')
            Object.keys(pouleStandings).forEach(pouleId => {
                const teams = pouleStandings[pouleId]
                teams.forEach(te => {
                    statsText += t('presentation_page.predictor_stats_row', {
                        name: te.name,
                        points: te.points,
                        goalDifference: te.goalDifference
                    })
                })
            })

            const prompt = t('presentation_page.predictor_prompt', {
                stats: statsText,
                language: i18n.language
            })

            const response = await askGemini(prompt)
            const jsonStr = response.replace(/```json/g, '').replace(/```/g, '').trim()
            setPrediction(JSON.parse(jsonStr))

        } catch (err) {
            console.error(err)
            // Fallback
            setPrediction({
                teamName: t('presentation_page.fallback_team'),
                percentage: "100%",
                reason: t('presentation_page.fallback_reason')
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex flex-col items-center justify-center p-8 min-h-[500px] animate-fade-in text-center">

            {!prediction && !loading && (
                <div className="max-w-xl space-y-8">
                    <div className="relative mx-auto w-48 h-48 bg-purple-500/10 rounded-full flex items-center justify-center border-4 border-purple-500 animate-pulse-slow">
                        <Sparkles size={80} className="text-purple-600" />
                    </div>
                    <h2 className="text-4xl font-black text-gray-900">{t('presentation_page.predictor_title')}</h2>
                    <p className="text-xl text-gray-600">
                        {t('presentation_page.predictor_desc')}
                    </p>
                    <Button
                        onClick={predictWinner}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-2xl py-6 px-12 rounded-2xl shadow-2xl transition-transform hover:scale-105"
                    >
                        {t('presentation_page.btn_predict')}
                    </Button>
                </div>
            )}

            {loading && (
                <div className="flex flex-col items-center gap-4">
                    <Loader2 size={64} className="text-purple-600 animate-spin" />
                    <p className="text-2xl font-bold text-gray-600 animate-pulse">{t('presentation_page.loading_predict')}</p>
                </div>
            )}

            {prediction && (
                <div className="animate-scaleIn max-w-2xl bg-gradient-to-br from-purple-900 to-indigo-900 text-white p-12 rounded-3xl shadow-[0_20px_60px_rgba(88,28,135,0.5)] border border-purple-400/30 relative overflow-hidden">
                    {/* Confetti or glow effect */}
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid-pattern.svg')] opacity-20"></div>
                    <div className="absolute top-[-50px] right-[-50px] w-64 h-64 bg-purple-500 rounded-full blur-[100px] opacity-50"></div>

                    <div className="relative z-10 flex flex-col items-center">
                        <Trophy size={80} className="text-yellow-400 mb-6 drop-shadow-lg animate-bounce-slow" />
                        <h3 className="text-2xl uppercase tracking-widest font-bold text-purple-300 mb-2">{t('presentation_page.prediction_title')}</h3>
                        <h1 className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-yellow-500 drop-shadow-lg mb-6">
                            {prediction.teamName}
                        </h1>
                        <div className="flex items-center gap-4 mb-8">
                            <span className="text-4xl font-bold text-white bg-white/20 px-6 py-2 rounded-xl backdrop-blur-md">
                                {prediction.percentage} {t('presentation_page.prediction_chance')}
                            </span>
                        </div>
                        <p className="text-xl md:text-2xl font-medium text-purple-100 italic leading-relaxed">
                            "{prediction.reason}"
                        </p>

                        <Button
                            onClick={() => setPrediction(null)}
                            variant="ghost"
                            className="mt-12 text-purple-300 hover:text-white"
                        >
                            {t('presentation_page.btn_retry')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
